/**
 * FeaturedShelvesCard — admin CRUD for the /shop featured shelves.
 *
 * Fetches the current shelf list, lets admins:
 *   - add / remove shelves
 *   - reorder them (up/down arrows — no drag-drop dep)
 *   - edit title, subtitle, accent, filter recipe (raw params textarea)
 *   - toggle enabled per shelf
 *   - reset to shipped defaults
 *
 * Filter recipe uses a simple `k=v&k=v` textarea so admins can compose
 * anything the public `/products` endpoint supports (max_price, brand,
 * sort=trending, sub_category, min_score, limit, …). We validate it as
 * URLSearchParams-parseable before saving.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LayoutList, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Save, Loader2 } from "lucide-react";

interface Shelf {
  id: string;
  title: string;
  subtitle: string;
  accent: string;
  params: Record<string, any>;
  enabled: boolean;
  sort_order?: number;
}

const ACCENTS = ["saffron", "emerald", "rose", "navy"];

function paramsToText(p: Record<string, any>): string {
  return Object.entries(p || {}).map(([k, v]) => `${k}=${v}`).join("&");
}

function textToParams(t: string): Record<string, any> {
  const out: Record<string, any> = {};
  new URLSearchParams(t).forEach((v, k) => {
    // Preserve numeric where possible (limit=8 stays a number in JSON)
    if (/^-?\d+$/.test(v)) out[k] = parseInt(v);
    else out[k] = v;
  });
  return out;
}

function newShelf(): Shelf {
  return {
    id: `shelf_${Math.random().toString(36).slice(2, 10)}`,
    title: "New shelf",
    subtitle: "",
    accent: "saffron",
    params: { sort: "score", limit: 8 },
    enabled: true,
  };
}

export const FeaturedShelvesCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<{ shelves: Shelf[] }>({
    queryKey: ["admin-featured-shelves"],
    queryFn: async () => (await api.get("/admin/featured-shelves")).data,
  });
  const [shelves, setShelves] = useState<Shelf[]>([]);

  useEffect(() => {
    if (q.data?.shelves) setShelves(q.data.shelves);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/featured-shelves", { shelves })).data,
    onSuccess: () => {
      toast("Shelves updated — live on /shop instantly");
      qc.invalidateQueries({ queryKey: ["admin-featured-shelves"] });
      qc.invalidateQueries({ queryKey: ["public-featured-shelves"] });
    },
    onError: () => toast("Failed to save shelves"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/featured-shelves/reset")).data,
    onSuccess: (r) => {
      setShelves(r.shelves || []);
      toast("Restored default shelves");
      qc.invalidateQueries({ queryKey: ["public-featured-shelves"] });
    },
  });

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= shelves.length) return;
    const next = [...shelves];
    [next[i], next[j]] = [next[j], next[i]];
    setShelves(next);
  };

  const patch = (i: number, p: Partial<Shelf>) => {
    setShelves(shelves.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  };

  return (
    <div className="sg-card p-6" data-testid="featured-shelves-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
          <LayoutList className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">
            Featured Shelves
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5 leading-snug">
            Curated rows shown above the /shop grid on the cold landing view.
            Each shelf runs its own filter recipe. Reorder with ▲▼, disable
            without deleting, or restore the shipped defaults any time.
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {shelves.map((s, i) => (
          <div
            key={s.id}
            data-testid={`admin-shelf-${s.id}`}
            className="border border-[color:var(--sg-border)] p-4 bg-[color:var(--sg-muted-bg)]/30"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--sg-muted)]">#{i + 1}</span>
              <Switch
                checked={s.enabled}
                onCheckedChange={(v: boolean) => patch(i, { enabled: v })}
                data-testid={`admin-shelf-${s.id}-enabled`}
              />
              <span className="text-xs text-[color:var(--sg-muted)]">{s.enabled ? "Live" : "Hidden"}</span>
              <div className="flex-1" />
              <button onClick={() => move(i, -1)} disabled={i === 0} className="h-7 w-7 flex items-center justify-center border border-[color:var(--sg-border)] disabled:opacity-40" aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, +1)} disabled={i === shelves.length - 1} className="h-7 w-7 flex items-center justify-center border border-[color:var(--sg-border)] disabled:opacity-40" aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => setShelves(shelves.filter((_, j) => j !== i))} className="h-7 w-7 flex items-center justify-center border border-rose-200 dark:border-rose-900 text-rose-600 dark:text-rose-400" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider mb-1 block">Title</Label>
                <Input value={s.title} onChange={(e) => patch(i, { title: e.target.value })} data-testid={`admin-shelf-${s.id}-title`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider mb-1 block">Accent color</Label>
                <select
                  value={s.accent}
                  onChange={(e) => patch(i, { accent: e.target.value })}
                  data-testid={`admin-shelf-${s.id}-accent`}
                  className="h-9 w-full px-2 border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] text-[color:var(--sg-fg)] text-sm"
                >
                  {ACCENTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider mb-1 block">Subtitle</Label>
                <Input value={s.subtitle} onChange={(e) => patch(i, { subtitle: e.target.value })} data-testid={`admin-shelf-${s.id}-subtitle`} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider mb-1 block">
                  Filter recipe <span className="font-mono normal-case text-[10px]">(k=v&amp;k=v — supports max_price, min_score, brand, sub_category, sort=trending|score|reviewed|price_low|price_high, limit)</span>
                </Label>
                <Textarea
                  rows={2}
                  value={paramsToText(s.params)}
                  onChange={(e) => {
                    try { patch(i, { params: textToParams(e.target.value) }); }
                    catch {}
                  }}
                  data-testid={`admin-shelf-${s.id}-params`}
                  className="font-mono text-xs"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--sg-border)] pt-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShelves([...shelves, newShelf()])}
            data-testid="admin-shelf-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add shelf
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (window.confirm("Restore the shipped-with-app default shelves? Your edits will be lost.")) reset.mutate();
            }}
            disabled={reset.isPending}
            data-testid="admin-shelf-reset"
          >
            {reset.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
            Reset to defaults
          </Button>
        </div>
        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="admin-shelf-save"
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default FeaturedShelvesCard;
